import { useState } from 'react'

import { ActionIcon, Badge, Box, Menu, TextInput } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconChevronLeft from 'src/icons/IconChevronLeft'
import IconFilter from 'src/icons/IconFilter'
import IconSearch from 'src/icons/IconSearch'
import { formatSnakeValue } from 'src/lib/formatters'

import { updateUrl } from '../../../pages/CallAnalyticsPage/utils'

const FilterMenu = ({ filters }) => {
  const [selectedSubmenu, setSelectedSubmenu] = useState('')
  const [searchText, setSearchText] = useState('')
  // Which nested parents are expanded, keyed as `${filterTitle}:${optionValue}`
  // so multiple nested filters don't share expansion state.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  const getTotalSelectedCount = () => {
    return filters.reduce(
      (total, filter) => total + (filter.selected?.length || 0),
      0
    )
  }

  const getDisplayValue = (filter, value) => {
    if (filter.formatValue) {
      return filter.formatValue(value)
    }
    if (filter.options?.find) {
      return filter.options.find((opt) => opt.value === value)?.label
    }
    return formatSnakeValue(value)
  }

  const handleClear = (filter) => {
    filter.onChange([])
    updateUrl(filter.urlKey, '')
  }

  const handleClearAll = () => {
    filters.forEach((filter) => {
      filter.onChange([])
      updateUrl(filter.urlKey, '')
    })
  }

  const enterSubmenu = (filter) => {
    setSelectedSubmenu(filter.title.toLowerCase())
    setSearchText('')
    // For nested filters, pre-expand any group that already has a selection
    // (a bare "TYPE" or a "TYPE:name" child) so active choices are visible on
    // reopen instead of hidden behind collapsed parents.
    const expanded = new Set<string>()
    if (filter.isNested) {
      for (const value of filter.selected || []) {
        const separator = value.indexOf(':')
        const parent = separator === -1 ? value : value.slice(0, separator)
        expanded.add(`${filter.title}:${parent}`)
      }
    }
    setExpandedGroups(expanded)
    // Lazily load options for async filters (e.g. company lead sources) on
    // open. The hook itself decides whether to fetch (first open) or retry
    // (after a prior failure) — so we don't gate on isLoaded here, which would
    // block retries once a failed load marked the filter "loaded".
    filter.loadData?.()
  }

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  // Renders the flat list of options for a (possibly nested) filter. For nested
  // filters this is the leaf/child renderer used inside each expanded group.
  const renderFlatOptions = (activeFilter, options) => {
    return options.map((option) => (
      <Box
        key={option.value}
        className="flex flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-100"
        onClick={() => {
          const newSelected = activeFilter.selected.includes(option.value)
            ? activeFilter.selected.filter((item) => item !== option.value)
            : [...activeFilter.selected, option.value]
          activeFilter.onChange(newSelected)
        }}
      >
        <span className="flex items-center gap-2">
          {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
          {option.label}
        </span>
        {activeFilter.selected.includes(option.value) && (
          <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
        )}
      </Box>
    ))
  }

  // Nested drill-down: each top-level option is a group (e.g. a lead-source
  // link type) that expands to an "All" row plus its child options (e.g.
  // specific link names). Child values are encoded as `${parent}:${child}`.
  const renderNestedOptions = (activeFilter, options) => {
    const search = searchText.trim().toLowerCase()
    const groups = options.filter((group) => {
      if (!search) return true
      if (group.label.toLowerCase().includes(search)) return true
      return (group.options || []).some((child) =>
        child.label.toLowerCase().includes(search)
      )
    })

    if (groups.length === 0) {
      return (
        <div className="flex items-center justify-center p-4 text-xs text-doubleNickel-gray-500">
          {search ? 'No matching options found' : 'No options found'}
        </div>
      )
    }

    return groups.map((group) => {
      const groupKey = `${activeFilter.title}:${group.value}`
      const parentMatches = !search || group.label.toLowerCase().includes(search)
      const allChildren = group.options || []
      // While searching, surface matches: force the group open and — unless the
      // type label itself matched — narrow to the children that matched.
      const children =
        search && !parentMatches
          ? allChildren.filter((child) =>
              child.label.toLowerCase().includes(search)
            )
          : allChildren
      const isExpanded = expandedGroups.has(groupKey) || !!search
      return (
        <div key={group.value} className="flex flex-col">
          <Box
            className="flex flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-100"
            onClick={() => toggleGroup(groupKey)}
          >
            <span>{group.label}</span>
            <IconChevronLeft
              className={`h-4 w-4 fill-none stroke-doubleNickel-gray-400 ${
                isExpanded ? 'rotate-90' : 'rotate-180'
              }`}
            />
          </Box>
          {isExpanded && (
            <div className="ml-4 flex flex-col">
              {/* "All of this type" — selects the bare parent value. */}
              <Box
                className="flex flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-50"
                onClick={() => {
                  const newSelected = activeFilter.selected.includes(
                    group.value
                  )
                    ? activeFilter.selected.filter(
                        (item) => item !== group.value
                      )
                    : [...activeFilter.selected, group.value]
                  activeFilter.onChange(newSelected)
                }}
              >
                <span className="italic text-doubleNickel-gray-600">All</span>
                {activeFilter.selected.includes(group.value) && (
                  <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                )}
              </Box>
              {children.map((child) => {
                const childValue = `${group.value}:${child.value}`
                return (
                  <Box
                    key={childValue}
                    className="flex flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-50"
                    onClick={() => {
                      const newSelected = activeFilter.selected.includes(
                        childValue
                      )
                        ? activeFilter.selected.filter(
                            (item) => item !== childValue
                          )
                        : [...activeFilter.selected, childValue]
                      activeFilter.onChange(newSelected)
                    }}
                  >
                    {child.label}
                    {activeFilter.selected.includes(childValue) && (
                      <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                    )}
                  </Box>
                )
              })}
            </div>
          )}
        </div>
      )
    })
  }

  const menuContents = () => {
    if (selectedSubmenu) {
      const activeFilter = filters.find(
        (f) => f.title.toLowerCase() === selectedSubmenu
      )
      if (!activeFilter) return null

      // Async filters show "Loading…" while the initial fetch is in flight,
      // instead of flashing "No options found".
      const isInitialLoading =
        activeFilter.loadData &&
        !activeFilter.isLoaded &&
        (activeFilter.options?.length ?? 0) === 0

      const showSearch = activeFilter.searchable || activeFilter.isNested

      let optionsToDisplay = [...(activeFilter.options || [])]

      // Sort options alphabetically if alphabetize is not explicitly set to false
      if (activeFilter.alphabetize !== false) {
        optionsToDisplay = optionsToDisplay.sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      }

      // Client-side search for flat (non-nested) filters — nested filters
      // filter groups+children inside renderNestedOptions.
      const flatOptions =
        !activeFilter.isNested && searchText.trim()
          ? optionsToDisplay.filter((opt) =>
              opt.label.toLowerCase().includes(searchText.trim().toLowerCase())
            )
          : optionsToDisplay

      return (
        <>
          {showSearch && (
            <TextInput
              size="xs"
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.currentTarget.value)}
              leftSection={
                <IconSearch className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-400" />
              }
              className="px-2 pb-1"
            />
          )}
          {isInitialLoading ? (
            <div className="flex items-center justify-center p-4 text-xs text-doubleNickel-gray-500">
              Loading…
            </div>
          ) : activeFilter.isNested ? (
            renderNestedOptions(activeFilter, optionsToDisplay)
          ) : flatOptions.length > 0 ? (
            renderFlatOptions(activeFilter, flatOptions)
          ) : (
            <div className="flex items-center justify-center p-4 text-xs text-doubleNickel-gray-500">
              {searchText.trim()
                ? 'No matching options found'
                : 'No options found'}
            </div>
          )}
        </>
      )
    }

    return (
      <>
        {filters
          .slice()
          .sort((a, b) => a.title.localeCompare(b.title))
          .map((filter) => (
            <Menu.Item
              key={filter.title}
              rightSection={
                <IconChevronLeft className="rotate-180 fill-none stroke-doubleNickel-gray-400" />
              }
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                enterSubmenu(filter)
              }}
            >
              <Box className="flex flex-row items-center justify-between py-1 text-xs font-medium text-doubleNickel-gray-800">
                {filter.title}
                <div className="font-normal text-doubleNickel-gray-500">
                  {filter.selected?.length > 0 &&
                    getDisplayValue(filter, filter.selected[0])}
                  {filter.selected?.length > 1 &&
                    `, ${filter.selected.length - 1}+`}
                </div>
              </Box>
            </Menu.Item>
          ))}
      </>
    )
  }

  return (
    <Menu
      shadow="md"
      width={275}
      position="bottom-end"
      withArrow
      arrowPosition="center"
      closeOnItemClick={false}
    >
      <Menu.Target>
        <DNButton variant="outline">
          <IconFilter className="h-4 fill-none stroke-doubleNickel-gray-700" />
          Filters
          <Badge size="xs" color="gray" variant="light">
            {getTotalSelectedCount()}
          </Badge>
        </DNButton>
      </Menu.Target>

      <Menu.Dropdown className="rounded-lg">
        <div className="flex flex-col gap-2 py-2">
          <div className="flex flex-row items-center pr-4 text-sm">
            {selectedSubmenu && (
              <ActionIcon
                variant="subtle"
                onClick={() => setSelectedSubmenu('')}
              >
                <IconChevronLeft className="fill-none stroke-doubleNickel-gray-500" />
              </ActionIcon>
            )}
            <Menu.Label className="capitalize">
              {selectedSubmenu || 'Filters'}
            </Menu.Label>
            {selectedSubmenu && (
              <Box
                className="ml-auto text-xs text-doubleNickel-brand-500 hover:cursor-pointer hover:underline"
                onClick={() => {
                  const filter = filters.find(
                    (f) => f.title.toLowerCase() === selectedSubmenu
                  )
                  if (filter) handleClear(filter)
                }}
              >
                Clear
              </Box>
            )}
            <Box
              className={`${
                selectedSubmenu ? 'ml-2' : 'ml-auto'
              } text-xs text-doubleNickel-brand-500 hover:cursor-pointer hover:underline`}
              onClick={handleClearAll}
            >
              Clear all
            </Box>
          </div>
          <div className="max-h-[450px] overflow-y-auto">{menuContents()}</div>
        </div>
      </Menu.Dropdown>
    </Menu>
  )
}

export default FilterMenu
